import { useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { ChevronLeftIcon, PrintIcon, DownloadIcon, FolderOpenIcon } from '@components/icons';
import { isDesktop } from '@/desktop/desktop';
import { revealLabel, revealRecord } from '@/desktop/reveal';
import type { Study } from '@skjv/common';
import { getChapterId } from '@skjv/common';
import { ThoughtsAccordion } from '@components/chat-panel/rich-message';
import { LocationDropTarget } from './location-drop-target';
import { AiCommentaryCue } from './ai-commentary-cue';
import { SceneEditor } from './scene-editor';
import { AssignmentEditor } from './assignment-editor';
import { TeacherAssignmentEditor } from './teacher-assignment-editor';
import { DownloadModal } from './download-modal';
import { StudyInstructions } from './study-instructions';
import {
  handleBack,
  handleNameChange,
  handleGroupChange,
  handleStartingLocationDrop,
  handleEndingLocationDrop,
  handleWritingLocationDrop,
  handleMemoryStartDrop,
  handleMemoryEndDrop,
  handlePrint,
} from './study-editor-handlers';

/**
 * Study Editor - edit or create a study with auto-save.
 */
export const StudyEditor = observer(function StudyEditor() {
  const study = bibleState.editingStudy;
  const isNew = !study?.id || study.id === '';
  const studyGroups = bibleState.sortedStudyGroups;
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const saveStudy = useCallback(() => {
    if (study && study.name && study.startingLocation && study.studyGroupId) {
      bibleState.saveEditingStudy();
    }
  }, [study]);

  useEffect(() => {
    if (!study) return;
    const timer = setTimeout(saveStudy, 500);
    return () => clearTimeout(timer);
  }, [study, saveStudy]);

  if (!study) {
    return (
      <div className="py-8 text-center text-stone-500 dark:text-stone-400">No study selected</div>
    );
  }

  const studyGroup = study.studyGroupId ? bibleState.getStudyGroup(study.studyGroupId) : undefined;
  const showInstructions = isNew && !study.startingLocation;
  const canExport = !isNew && study.name && study.startingLocation && study.studyGroupId;
  const currentChapter = getChapterId(bibleState.currentLocation);
  const studyChapter = study.startingLocation ? getChapterId(study.startingLocation) : null;
  const showLoadPassage = !isNew && study.startingLocation && currentChapter !== studyChapter;

  return (
    <div className="study-editor flex h-full flex-col">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2 border-b border-stone-200/50 pb-3 dark:border-stone-700/50">
        <button
          onClick={handleBack}
          className="-ml-1.5 rounded-lg p-1.5 text-stone-500 transition-all hover:bg-stone-200/50 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700/30 dark:hover:text-stone-200"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <span className="font-medium text-stone-700 dark:text-stone-200">
          {isNew ? 'New Study' : 'Edit Study'}
        </span>
        {canExport && (
          <div className="ml-2 flex items-center gap-1">
            <button
              onClick={() => handlePrint(study as Study)}
              className="rounded-lg p-1.5 text-stone-500 transition-all hover:bg-stone-200/50 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700/30 dark:hover:text-stone-200"
              title="Print"
            >
              <PrintIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowDownloadModal(true)}
              className="rounded-lg p-1.5 text-stone-500 transition-all hover:bg-stone-200/50 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700/30 dark:hover:text-stone-200"
              title="Download"
            >
              <DownloadIcon className="h-4 w-4" />
            </button>
            {/* Desktop only: the study's folder on disk (JSON + md + PDF). */}
            {isDesktop() && study.id && (
              <button
                onClick={() => void revealRecord('studies', study.id as string)}
                className="rounded-lg p-1.5 text-stone-500 transition-all hover:bg-stone-200/50 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700/30 dark:hover:text-stone-200"
                title={revealLabel()}
                data-testid="study-reveal"
              >
                <FolderOpenIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {showLoadPassage && (
        <button
          onClick={() => study.startingLocation && bibleState.setCurrentLocation(study.startingLocation)}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-300/50 bg-cyan-50/80 px-3 py-2 text-sm font-medium text-cyan-700 transition-all hover:bg-cyan-100/80 dark:border-cyan-700/40 dark:bg-cyan-900/20 dark:text-cyan-300 dark:hover:bg-cyan-900/40"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          Load Passage
        </button>
      )}

      {showDownloadModal && study.id && (
        <DownloadModal study={study as Study} onClose={() => setShowDownloadModal(false)} />
      )}

      {/* Scrollable content */}
      <div className="flex-1 space-y-4 overflow-y-auto pr-1">
        {showInstructions ? (
          <StudyInstructions />
        ) : (
          <>
            {/* Study Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-600 dark:text-stone-400">Study Name</label>
              <input
                type="text"
                value={study.name || ''}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., John 1:1-5 - In the Beginning"
                className="w-full rounded-lg border border-stone-300/50 bg-white/80 px-3 py-2 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600/50 dark:bg-stone-800/60 dark:text-stone-200 dark:placeholder-stone-500"
              />
            </div>

            {/* Study Group */}
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-600 dark:text-stone-400">Study Group</label>
              <select
                value={study.studyGroupId || ''}
                onChange={(e) => handleGroupChange(e.target.value)}
                className="w-full rounded-lg border border-stone-300/50 bg-white/80 px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600/50 dark:bg-stone-800/60 dark:text-stone-200 dark:[color-scheme:dark]"
              >
                <option value="">Select a group...</option>
                {studyGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.students.length} students, {(group.teachers || []).length} teachers)
                  </option>
                ))}
              </select>
            </div>

            {/* Passage Range */}
            <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
              <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">Passage to Study</h3>
              <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">Drag verse labels from the Bible text into these areas</p>
              <div className="grid grid-cols-2 gap-2">
                <LocationDropTarget label="Starting Verse" location={study.startingLocation} onDrop={handleStartingLocationDrop} required dataGuide="starting-verse" />
                <LocationDropTarget label="Ending Verse" location={study.endingLocation} onDrop={handleEndingLocationDrop} placeholder="(whole chapter)" />
              </div>
              <AiCommentaryCue />
            </div>

            {/* Reader thoughts (experimental) — seeds AI commentary, prints
                on the teacher pages, persists with the study. Draws its own
                panel, so no wrapper card here. */}
            <ThoughtsAccordion
              value={study.readerThoughts ?? ''}
              onChange={(readerThoughts) =>
                bibleState.updateEditingStudy({ readerThoughts: readerThoughts || undefined })}
            />

            {/* Writing Location */}
            <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
              <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">Writing Practice Verse</h3>
              <LocationDropTarget label="Write this verse" location={study.writingLocation} onDrop={handleWritingLocationDrop} />
            </div>

            {/* Memory Verse */}
            <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
              <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">Memory Verse</h3>
              <div className="grid grid-cols-2 gap-2">
                <LocationDropTarget label="Start" location={study.memoryStartingLocation ?? studyGroup?.memoryStartingLocation} onDrop={handleMemoryStartDrop} placeholder="(none)" />
                <LocationDropTarget label="End" location={study.memoryEndingLocation ?? studyGroup?.memoryEndingLocation} onDrop={handleMemoryEndDrop} placeholder="(same)" />
              </div>
            </div>

            <SceneEditor scenes={study.scenes || []} onChange={(scenes) => bibleState.updateEditingStudy({ scenes })} />
            <AssignmentEditor assignments={study.assignments || []} studyGroupId={study.studyGroupId} onChange={(assignments) => bibleState.updateEditingStudy({ assignments })} />
            <TeacherAssignmentEditor teacherAssignments={study.teacherAssignments || []} studyGroupId={study.studyGroupId} onChange={(teacherAssignments) => bibleState.updateEditingStudy({ teacherAssignments })} />
          </>
        )}

        {showInstructions && (
          <div className="mt-4">
            <LocationDropTarget label="Starting Verse" location={study.startingLocation} onDrop={handleStartingLocationDrop} required large dataGuide="starting-verse" />
          </div>
        )}
      </div>
    </div>
  );
});
